import { Film, Download, Save, Loader2 } from 'lucide-react';
import { cn } from '@/lib/utils';
import type { IRenderAreaProps } from '@/@types/photostudio';

export function RenderArea({
  imageUrl,
  isLoading = false,
  label = 'Primary Render',
  onDownload,
  onSave,
}: IRenderAreaProps) {
  const hasImage = !!imageUrl;
  const showOverlay = hasImage && !isLoading && (onDownload || onSave);

  return (
    <div
      className={cn(
        'group relative flex-1 min-h-[300px] rounded-lg border overflow-hidden',
        'bg-card border-border',
        'flex items-center justify-center'
      )}
    >
      {isLoading ? (
        <div className="flex flex-col items-center justify-center gap-3">
          <Loader2 className="h-10 w-10 animate-spin text-foreground-muted" />
          <span className="text-sm text-foreground-muted">Generating...</span>
        </div>
      ) : hasImage ? (
        <>
          <img src={imageUrl} alt={label} className="h-full w-full object-cover" />
          {showOverlay && (
            <div
              className={cn(
                'absolute inset-0 flex items-center justify-center gap-3',
                'bg-black/60 opacity-0 transition-opacity',
                'group-hover:opacity-100'
              )}
            >
              {onDownload && (
                <button
                  type="button"
                  onClick={onDownload}
                  className={cn(
                    'flex h-10 w-10 items-center justify-center rounded-full',
                    'bg-white/10 text-white hover:bg-white/20',
                    'transition-colors'
                  )}
                  aria-label="Download image"
                >
                  <Download className="h-5 w-5" />
                </button>
              )}
              {onSave && (
                <button
                  type="button"
                  onClick={onSave}
                  className={cn(
                    'flex h-10 w-10 items-center justify-center rounded-full',
                    'bg-white/10 text-white hover:bg-white/20',
                    'transition-colors'
                  )}
                  aria-label="Save image"
                >
                  <Save className="h-5 w-5" />
                </button>
              )}
            </div>
          )}
        </>
      ) : (
        <div className="flex flex-col items-center justify-center gap-3">
          <Film className="h-12 w-12 text-foreground-placeholder" />
          <span className="font-mono text-[11px] font-medium uppercase tracking-[2px] text-foreground-placeholder">
            No image generated
          </span>
        </div>
      )}
    </div>
  );
}
